import type { OpportunityListItem } from '@opportunities/list/-hooks/useOpportunities'
import { execSearchQuery } from '@/lib/search-utils'
import { CustomSearchCaptain } from './custom-search-captain'

const SEARCHABLE_KEYS: (keyof OpportunityListItem)[] = [
  'id',
  'title',
  'opportunity_type_name',
  'opportunity_status_name',
  'opportunity_close_reason',
  'opportunity_additional_close_reason',
  'created_by_name',
  'created_by_employee_number',
  'created_by_division_name',
  'short_created_by_division_name',
  'salesforce_project_name',
  'ano',
  'description',
  'customer_name',
  'contact_name',
  'sales_recording_group_name',
  'start_on',
  'information_source',
  'vertical_division_name',
  'short_vertical_division_name',
  'lateral_division_name',
  'short_lateral_division_name',
  'comment_count',
  'task_count',
]

const NAME_KEYS = new Set<keyof OpportunityListItem>(['customer_name', 'contact_name', 'created_by_name'])

/**
 * オポチュニティをカスタム検索クエリでフィルタリング
 *
 * @param opportunities - フィルタリング対象のオポチュニティリスト
 * @param searchText - 検索クエリ文字列
 * @returns フィルタリングされたオポチュニティリスト
 */
export function filterOpportunities(opportunities: OpportunityListItem[], searchText: string): OpportunityListItem[] {
  if (!searchText) return opportunities

  const customSearch = new CustomSearchCaptain()
  customSearch.parse(searchText)

  return opportunities.filter((item) => customFilter(item, searchText, customSearch))
}

/**
 * 個別オポチュニティに対するカスタムフィルター
 *
 * @param item - フィルタリング対象のオポチュニティ
 * @param searchText - 検索クエリ文字列
 * @param customSearch - パース済みの検索オブジェクト
 * @returns フィルター条件に一致する場合はtrue
 */
function customFilter(item: OpportunityListItem, searchText: string, customSearch?: CustomSearchCaptain): boolean {
  if (!searchText) return true

  const search = customSearch || new CustomSearchCaptain().parse(searchText)

  // タグフィルタリング
  const tags = item.tags || []

  // 含むタグのチェック
  for (const tag of search.tags) {
    const fullTagName = tag.group ? `${tag.group}:${tag.name}` : tag.name
    const hasTag = tags.some((t: { id: number; name: string; color: string | null }) => {
      const itemTagName = t.color ? `${t.color}:${t.name}` : t.name
      return itemTagName === fullTagName || t.name === tag.name
    })
    if (!hasTag) return false
  }

  // 除外タグのチェック
  for (const tag of search.excludeTags) {
    const fullTagName = tag.group ? `${tag.group}:${tag.name}` : tag.name
    const hasTag = tags.some((t: { id: number; name: string; color: string | null }) => {
      const itemTagName = t.color ? `${t.color}:${t.name}` : t.name
      return itemTagName === fullTagName || t.name === tag.name
    })
    if (hasTag) return false
  }

  // キーワード検索（含む）
  for (const query of search.queries) {
    if (!execSearchQuery(item, query, SEARCHABLE_KEYS, NAME_KEYS)) return false
  }

  // キーワード検索（除外）
  for (const exclude of search.excludes) {
    if (execSearchQuery(item, exclude, SEARCHABLE_KEYS, NAME_KEYS)) return false
  }

  // 取引先フィルター
  if (search.customerId && item.customer_id !== search.customerId) return false

  // 縦組織フィルター
  if (search.verticalDivisionId && item.vertical_division_id !== search.verticalDivisionId) return false

  // 横組織フィルター
  if (search.lateralDivisionId && item.lateral_division_id !== search.lateralDivisionId) return false

  // 起票者フィルター
  if (search.createdBy && item.created_by !== search.createdBy) return false

  // 売上計上グループフィルター
  if (search.salesRecordingGroupId && item.sales_recording_group_id !== search.salesRecordingGroupId) return false

  // ステータスフィルター
  if (search.opportunityStatusIds.length > 0 && !search.opportunityStatusIds.includes(item.opportunity_status_id))
    return false

  // インセンティブフィルター
  if (search.incentiveFilter === 'target' && item.disabled_at) return false
  if (search.incentiveFilter === 'disabled' && !item.disabled_at) return false

  // オポ種別フィルター
  if (search.opportunityTypeId && item.opportunity_type_id !== search.opportunityTypeId) return false

  return true
}
